import { useCallback, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useNavigate } from "react-router";

import { InboundingListProduct, ListProductStatus } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import {
  GetProductsQuery,
  useGetProductsLazyQuery,
} from "flows/Inbound/queries/collaborativeInbound/collaborativeInbound.generated";
import { useGetUnitsSizesLazyQuery } from "flows/Inbound/queries/product/product.generated";
import { InboundUnitAddOrigin, TranslatedProduct } from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import {
  getDisplayUnitSize,
  getUniqueHandlingUnitSizes,
} from "flows/Inbound/stores/inboundStore/utils";
import { useSharedListCreator } from "flows/Inbound/stores/useSharedListCreator";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { translatedInboundUnitFromSearchResult } from "shared/models/productSearchUnit/serializer";
import { TranslatedProductSearchUnit } from "shared/models/productSearchUnit/types";
import {
  SearchUnitsByEanQuery,
  useSearchUnitsByEanLazyQuery,
} from "shared/queries/productSearchUnit/productSearchUnit.generated";
import { useProductSearchStore } from "shared/stores/useProductSearchStore";
import { countryNameFromCode } from "utils/countryNames";
import { getShelfDetail, sortByShelf } from "utils/item";

export const GET_UNITS_BY_EAN_ERROR_TOAST_ID = "get_units_by_ean_error_toast_id";
const GET_PRODUCTS_BY_SKUS_ERROR_TOAST_ID = "get_products_by_skus_error_toast_id";

export function useSerializedProduct() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);

  const transformProductForDisplay = useCallback(
    (
      product: GetProductsQuery["getProducts"]["products"][0],
      inboundingProduct?: InboundingListProduct,
    ): TranslatedProduct => {
      const shelf = product.inventoryEntry.shelfNumber;
      const shelfDetail = shelf ? getShelfDetail(shelf) : null;
      const {
        quantity = 0,
        handlingUnitSize = 1,
        status = null,
        isInbounded = null,
      } = inboundingProduct || {};
      const unitSizeForDisplay = getDisplayUnitSize(quantity, handlingUnitSize);

      return {
        productName: product.name,
        productSku: product.sku,
        productImageUrl: product.imageUrl ?? null,
        isProductBio: product.bio ?? null,
        countryOfOriginName: countryNameFromCode(
          appLanguage,
          product.countryOfOrigin.code?.toUpperCase() ?? null,
        ),
        shelfLetter: shelfDetail?.shelfLetter ?? null,
        shelfNumber: shelfDetail?.shelfNumber ?? null,
        totalInboundQuantity: quantity,
        handlingUnitSize,
        unitSizeForDisplay,
        status,
        isInbounded:
          status === ListProductStatus.list_product_status_inbounded || isInbounded === true,
        stockOnShelf: product.inventoryEntry.stock?.shelf || 0,
      };
    },
    [appLanguage],
  );

  return transformProductForDisplay;
}

export function useSerializedProductForPreDropping() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);

  const transformProductForPreDropping = useCallback(
    (
      product: GetProductsQuery["getProducts"]["products"][0],
      inboundingProduct?: InboundingListProduct,
    ): TranslatedProductSearchUnit => {
      const shelfDetail = product.inventoryEntry.shelfNumber
        ? getShelfDetail(product.inventoryEntry.shelfNumber)
        : null;
      const handlingUnitSize = inboundingProduct?.handlingUnitSize || 1;
      return {
        id: inboundingProduct?.id || "",
        quantity: inboundingProduct?.quantity || 0,
        productSku: inboundingProduct?.sku || "",
        productName: product.name,
        productImageUrl: inboundingProduct?.imageUrl || null,
        isProductBio: product.bio ?? null,
        unitType: handlingUnitSize > 1 ? "handling" : "single",
        shelfLetter: shelfDetail?.shelfLetter || null,
        shelfNumber: shelfDetail?.shelfNumber || null,
        shelf: product.inventoryEntry.shelfNumber || null,
        countryOfOriginCode: product.countryOfOrigin.code?.toUpperCase() || null,
        countryOfOriginName: countryNameFromCode(
          appLanguage,
          product.countryOfOrigin.code?.toUpperCase() || null,
        ),
        numberOfShelfFacings: null,
        numberOfShelfStackings: null,
        isShelvedInHandlingUnits: null,
        handlingUnitSize,
        stockOnShelf: product.inventoryEntry.stock?.shelf || 0,
      };
    },
    [appLanguage],
  );

  return transformProductForPreDropping;
}

export function useAddProductsToSelectedProducts() {
  const navigate = useNavigate();
  const { addItemToSharedList } = useSharedListCreator();
  const resetTextSearch = useProductSearchStore((state) => state.resetTextSearch);

  const addProducts = useCallback(
    (newProducts: TranslatedProductSearchUnit[], origin: InboundUnitAddOrigin) => {
      if (newProducts.length === 0) return;
      const {
        inboundUnitsSortedByDate,
        addInboundUnitToPreDroppingList,
        moveDuplicatedProductToTop,
      } = useInboundStore.getState();
      const existingProductIdsSet = new Set(inboundUnitsSortedByDate);
      newProducts.forEach((product) => {
        if (existingProductIdsSet.has(product.productSku)) {
          moveDuplicatedProductToTop(product.productSku);
        } else {
          addInboundUnitToPreDroppingList(product, false, origin);
          addItemToSharedList(product.productSku);
        }
      });
      resetTextSearch();
      navigate(routes.inbound.preDropping);
    },
    [addItemToSharedList, navigate, resetTextSearch],
  );

  return addProducts;
}

export function useAddProductToSelectedProducts() {
  const addProducts = useAddProductsToSelectedProducts();

  return useCallback(
    (newProduct: TranslatedProductSearchUnit | null, origin: InboundUnitAddOrigin) => {
      if (newProduct) {
        addProducts([newProduct], origin);
      }
    },
    [addProducts],
  );
}

export function useFetchProductsByEan(ctx?: string) {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const addProducts = useAddProductsToSelectedProducts();
  const [isScannedProductLoading, setIsScannedProductLoading] = useState(false);
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { processBarcode } = useBarcodeScanner();
  const setInboundUIState = useInboundStore((state) => state.setInboundUIState);
  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while fetching units by ean", { errorMessage });
      showToastUI({
        id: GET_UNITS_BY_EAN_ERROR_TOAST_ID,
        title: "get_units_by_ean_error_toast_id",
        description: "get_units_by_ean_error_toast_description",
      });
      sendSegmentTrackEvent("errorShown", {
        screen_name: PageName.INBOUND_PAGE,
        component_value: "scan_request_failed",
      });
    },
    [sendSegmentTrackEvent, showToastUI],
  );
  const [searchUnitsByEan] = useSearchUnitsByEanLazyQuery({
    fetchPolicy: "no-cache",
    onError: handleError,
  });

  const onScan = useCallback(
    async (scannedValue: string) => {
      const { skusWithUnconfirmedQuantities } = useInboundStore.getState();
      const hasUnconfirmedQuantities = skusWithUnconfirmedQuantities.length > 0;
      if (isScannedProductLoading) return;
      if (hasUnconfirmedQuantities) {
        setInboundUIState({ isUnverifiedQuantityModalVisible: true });
        return;
      }
      const ean = processBarcode(scannedValue);
      if (!ean) return;
      setIsScannedProductLoading(true);
      try {
        const { data } = await searchUnitsByEan({
          variables: {
            searchUnitsByEanInput: { ean },
            ...(ctx === "inbounding" && { isAlwaysInbound: true }),
          },
        });

        const units = data?.searchUnitsByEan?.units || [];
        if (!data || units.length === 0) {
          setInboundUIState({ isScanningFailedModalVisible: true });
          sendSegmentTrackEvent("errorShown", {
            screen_name: PageName.INBOUND_PAGE,
            component_value: "scanning_failed",
            component_content: scannedValue,
          });
          return;
        }

        const uniqueProductSkus = new Set<string>();
        // Filter out duplicate units
        const filteredUnits = units.reduce(
          (acc, unit) => {
            if (!uniqueProductSkus.has(unit.productSku)) {
              uniqueProductSkus.add(unit.productSku);
              acc.push(unit);
            }
            return acc;
          },
          [] as SearchUnitsByEanQuery["searchUnitsByEan"]["units"],
        );

        const translatedUnits = translatedInboundUnitFromSearchResult(filteredUnits, appLanguage);
        addProducts(translatedUnits, InboundUnitAddOrigin.Scan);
      } catch (error) {
        handleError(error);
      } finally {
        setIsScannedProductLoading(false);
      }
    },
    [
      addProducts,
      appLanguage,
      ctx,
      handleError,
      isScannedProductLoading,
      processBarcode,
      searchUnitsByEan,
      sendSegmentTrackEvent,
      setInboundUIState,
    ],
  );

  return { onScan, isScannedProductLoading };
}

export function useFetchUnitsSizesLazyQuery() {
  const { isFeatureEnabled: isListVerificationHUCheckEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.LIST_VERIFICATION_HU_CHECK,
  );

  const handleFetchError = useCallback((error: any) => {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    datadogRum.addError("Error while fetching Units Sizes", { errorMessage });
    return {};
  }, []);

  const [getUnitsSizes] = useGetUnitsSizesLazyQuery({
    fetchPolicy: "no-cache",
    onError: handleFetchError,
  });

  return useCallback(
    async (skus: string[], isAlwaysInbound?: boolean): Promise<Record<string, number[]>> => {
      if (!isListVerificationHUCheckEnabled) return {};
      try {
        const { data } = await getUnitsSizes({
          variables: {
            input: { skus },
            ...(isAlwaysInbound && { isAlwaysInbound: true }),
          },
        });

        const products = data?.getProducts?.products;

        if (!products || products.length === 0) return {};

        return products.reduce<Record<string, number[]>>((result, product) => {
          if (product?.sku) {
            result[product.sku] = getUniqueHandlingUnitSizes(product.units);
          }
          return result;
        }, {});
      } catch (error) {
        handleFetchError(error);
        return {};
      }
    },
    [getUnitsSizes, handleFetchError, isListVerificationHUCheckEnabled],
  );
}

export function useFetchInboundPreDroppingProducts() {
  const navigate = useNavigate();
  const [isLoadingPreDroppingProducts, setIsLoadingPreDroppingProducts] = useState(false);
  const { showToastUI } = useCustomToast();
  const serializeProduct = useSerializedProductForPreDropping();
  const addInboundUnitToPreDroppingList = useInboundStore(
    (state) => state.addInboundUnitToPreDroppingList,
  );

  const handleError = useCallback(
    (error: unknown) => {
      setIsLoadingPreDroppingProducts(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred.";
      datadogRum.addError("Error while fetching products by SKUs.", { errorMessage });
      showToastUI({
        id: GET_PRODUCTS_BY_SKUS_ERROR_TOAST_ID,
        title: "get_products_by_skus_error_toast_id",
      });
    },
    [showToastUI],
  );

  const [fetchProducts] = useGetProductsLazyQuery({
    fetchPolicy: "no-cache",
    onError: handleError,
  });

  const fetchAndProcessProducts = useCallback(
    async (skus: string[], inboundingMap: Map<string, InboundingListProduct>) => {
      try {
        const { data } = await fetchProducts({
          variables: { input: { skus }, isAlwaysInbound: true },
        });
        const newProducts = data?.getProducts.products;

        if (!newProducts?.length) {
          navigate(routes.inbound.preDropping);
          return;
        }

        const serializedSortedByShelfProduct = newProducts
          .map((product) => {
            const inboundingProduct = inboundingMap.get(product.sku);
            return serializeProduct(product, inboundingProduct);
          })
          .sort(sortByShelf);
        serializedSortedByShelfProduct.forEach((product) => {
          if (!product) return;
          addInboundUnitToPreDroppingList(product, true);
        });

        setIsLoadingPreDroppingProducts(false);
        navigate(routes.inbound.preDropping);
      } catch (error) {
        handleError(error);
      }
    },
    [addInboundUnitToPreDroppingList, fetchProducts, handleError, navigate, serializeProduct],
  );

  const fetchInboundPreDroppingProducts = useCallback(
    async (products?: InboundingListProduct[] | null) => {
      if (!products?.length) {
        navigate(routes.inbound.preDropping);
        return;
      }
      setIsLoadingPreDroppingProducts(true);

      const skus = products.map((product) => product.sku);
      const inboundingMap = new Map(products.map((p) => [p.sku, p]));

      await fetchAndProcessProducts(skus, inboundingMap);
    },
    [fetchAndProcessProducts, navigate],
  );

  return { fetchInboundPreDroppingProducts, isLoadingPreDroppingProducts };
}
